import { useState } from 'react';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Form, FormActions } from '@/components/forms/Form';
import { FormInput } from '@/components/forms/FormField';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

interface MfaSetupResponse {
  secret: string;
  qrCodeUrl: string;
  otpauthUrl: string;
}

interface MfaVerifySetupResponse {
  success: boolean;
  backupCodes: string[];
}

const verifySchema = z.object({
  code: z
    .string()
    .length(6, 'Code must be 6 digits')
    .regex(/^\d+$/, 'Code must contain only digits'),
});

type VerifyFormValues = z.infer<typeof verifySchema>;

/**
 * MFA Setup page for back-office users.
 * Displays a QR code for authenticator app scanning and verifies the TOTP code.
 * Shows backup codes after successful setup.
 *
 * Validates: Requirement 2.2
 */
export function MfaSetup() {
  const navigate = useNavigate();
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [setupComplete, setSetupComplete] = useState(false);

  // Fetch MFA setup data (secret + QR code)
  const {
    data: setupData,
    isLoading: isSetupLoading,
    error: setupError,
  } = useQuery({
    queryKey: ['mfa-setup'],
    queryFn: async () => {
      const response = await apiClient.post<ApiResponse<MfaSetupResponse>>('/auth/mfa/setup');
      return response.data.data;
    },
  });

  // Verify TOTP code to complete setup
  const verifyMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await apiClient.post<ApiResponse<MfaVerifySetupResponse>>(
        '/auth/mfa/verify-setup',
        { code },
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      setSetupComplete(true);
    },
    onError: () => {
      setVerifyError('Invalid code. Please check your authenticator app and try again.');
    },
  });

  function handleVerify(values: VerifyFormValues) {
    setVerifyError(null);
    verifyMutation.mutate(values.code);
  }

  // Show backup codes after successful setup
  if (setupComplete && backupCodes) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface px-4 dark:bg-surface-dark">
        <Card className="w-full max-w-md" padding="lg">
          <CardHeader
            title="MFA Enabled Successfully"
            description="Save these backup codes in a secure location. Each code can only be used once."
          />
          <CardContent>
            <div className="rounded-lg border border-border bg-surface-secondary p-4 dark:border-border-dark dark:bg-surface-dark-tertiary">
              <ul className="grid grid-cols-2 gap-2" aria-label="Backup codes">
                {backupCodes.map((code, index) => (
                  <li
                    key={index}
                    className="rounded bg-white px-3 py-1.5 text-center font-mono text-sm text-gray-900 dark:bg-surface-dark-secondary dark:text-gray-100"
                  >
                    {code}
                  </li>
                ))}
              </ul>
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              If you lose access to your authenticator app, you can use these codes to sign in.
            </p>
            <div className="mt-6">
              <Button onClick={() => navigate('/settings/security')} className="w-full">
                Done
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4 dark:bg-surface-dark">
      <Card className="w-full max-w-md" padding="lg">
        <CardHeader
          title="Set Up Two-Factor Authentication"
          description="Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.)"
        />
        <CardContent>
          {isSetupLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
            </div>
          )}

          {setupError && (
            <div
              className="rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700 dark:border-danger-800 dark:bg-danger-900/20 dark:text-danger-400"
              role="alert"
            >
              Failed to initialize MFA setup. Please try again.
            </div>
          )}

          {setupData && (
            <>
              {/* QR Code Display */}
              <div className="flex flex-col items-center py-4">
                <div className="rounded-lg border border-border bg-white p-4 dark:border-border-dark">
                  <img
                    src={setupData.qrCodeUrl}
                    alt="Scan this QR code with your authenticator app"
                    className="h-48 w-48"
                  />
                </div>
              </div>

              {/* Manual entry secret */}
              <div className="mb-6">
                <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                  Can't scan the QR code? Enter this key manually:
                </p>
                <div className="rounded-lg border border-border bg-surface-secondary px-3 py-2 dark:border-border-dark dark:bg-surface-dark-tertiary">
                  <code className="break-all text-sm font-medium text-gray-900 dark:text-gray-100">
                    {setupData.secret}
                  </code>
                </div>
              </div>

              {/* Verification form */}
              {verifyError && (
                <div
                  className="mb-4 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700 dark:border-danger-800 dark:bg-danger-900/20 dark:text-danger-400"
                  role="alert"
                >
                  {verifyError}
                </div>
              )}

              <Form<VerifyFormValues>
                schema={verifySchema}
                defaultValues={{ code: '' }}
                onSubmit={handleVerify}
              >
                {(form) => (
                  <>
                    <FormInput
                      name="code"
                      control={form.control}
                      label="Enter the 6-digit code from your app"
                      placeholder="000000"
                      maxLength={6}
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      required
                    />
                    <FormActions>
                      <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                        Cancel
                      </Button>
                      <Button type="submit" loading={verifyMutation.isPending}>
                        Verify & Enable
                      </Button>
                    </FormActions>
                  </>
                )}
              </Form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
